import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import useAPI from '../hooks/useAPI';
import env from '../utils/env';

interface UserProfile {
    roles?: string[];
    role?: string[];
}

interface UserRoleContextValue {
    roles: string[];
    isLoading: boolean;
    error: string | null;
    hasRole: (role: string) => boolean;
    isAdmin: () => boolean;
}

const UserRoleContext = createContext<UserRoleContextValue | null>(null);

export function UserRoleProvider({ children }: Readonly<{ children: React.ReactNode }>) {
    const auth = useAuth();
    const api = useAPI();
    // Keep a stable ref to api so we don't need it in the effect's dep array
    const apiRef = useRef(api);
    apiRef.current = api;

    const [roles, setRoles] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const fetchedRef = useRef(false);

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.user || fetchedRef.current) return;

        fetchedRef.current = true;
        setIsLoading(true);

        apiRef.current.Get<UserProfile>('/users/me')
            .then((userProfile) => {
                setRoles(userProfile?.roles || userProfile?.role || []);
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Failed to fetch user roles');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [auth.isAuthenticated, auth.user]);

    const hasRole = useCallback((role: string) => roles.includes(role), [roles]);
    const isAdmin = useCallback(() => {
        if (env.disableAuth) {
            return true;
        }
        return hasRole('ROLE_ADMIN');
    }, [hasRole]);

    const value = useMemo(
        () => ({ roles, isLoading, error, hasRole, isAdmin }),
        [roles, isLoading, error, hasRole, isAdmin],
    );

    return <UserRoleContext.Provider value={value}>{children}</UserRoleContext.Provider>;
}

export function useUserRoleContext(): UserRoleContextValue {
    const ctx = useContext(UserRoleContext);
    if (!ctx) throw new Error('useUserRoleContext must be used within UserRoleProvider');
    return ctx;
}